// =============================================================
// Content model — static sections + admin-editable pages
// =============================================================
//
// The site has two kinds of content:
//
//   1. STATIC sections  — defined in i18n JSON, rendered as fixed
//      sections (hero, about, disciplines, locations, contact).
//
//   2. DYNAMIC pages    — created via the admin, stored in D1 (or
//      localStorage in dev), rendered as additional sections.
//
// Both kinds share a uniform SEO contract: title + description,
// auto-generated from content unless manually overridden.

import type { Locale } from './i18n';

export type LocalisedString = Record<Locale, string>;

export interface SeoOverride {
  /** Whether the manual override is active for this page. */
  enabled: boolean;
  title?: string;
  description?: string;
  ogImage?: string;
  /** Override per-locale fields if needed. */
  perLocale?: Partial<Record<Locale, { title?: string; description?: string; ogImage?: string }>>;
}

/** A page that can be edited through the admin. */
export interface PageRecord {
  id: string;
  slug: string;
  navLabel: LocalisedString;
  showInNav: boolean;
  navOrder: number;
  /** Simple Markdown body. The renderer converts to safe HTML. */
  body: LocalisedString;
  /** Optional manual SEO override (auto-gen is the default). */
  seo?: SeoOverride;
  createdAt?: string;
  updatedAt?: string;
}

/** Agenda event — admin-defined calendar items. */
export type AgendaEventKind = 'open' | 'event' | 'closed' | 'taken';
export interface AgendaEvent {
  id: string;
  title: LocalisedString;
  startsAt: string;   // ISO 8601
  endsAt: string;     // ISO 8601
  location: 'A22' | 'M15';
  kind: AgendaEventKind;
  capacity: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  // Populated only for synthetic "taken" events that came from an
  // approved booking — never set on admin-defined events. The
  // server returns these only when the request is authenticated
  // as an admin (Bearer token), so they're safe to surface in the
  // admin UI without leaking PII on the public site.
  source?: 'booking';
  bookingId?: string;
  occurrenceId?: string;
  userFirstName?: string;
  userLastName?: string;
}

// -----------------------------------------------------------
// USERS / TRAINEES
// -----------------------------------------------------------

/** Site flavor — the strength/yoga brand axis. A user's `discipline`
 *  is the authoritative value after login. */
export type Discipline = 'strength' | 'yoga';

/** A trainee (customer) account. Same human referred to as `user`. */
export interface UserAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  active: boolean;
  isAdmin: boolean;
  passwordChangeRequired: boolean;
  notes?: string;
  /** Site flavor (strength | yoga). Drives the booking dropdown filter
   *  and the post-login mode re-assertion. */
  discipline?: Discipline;
  createdAt?: string;
  updatedAt?: string;
}

/** Subset of UserAccount returned to the user about themselves. */
export interface UserSession {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  isAdmin: boolean;
  passwordChangeRequired: boolean;
  /** Discipline is always present on a live session. Defaulted to
   *  'strength' for legacy cached users that pre-date 0003. */
  discipline: Discipline;
}

// -----------------------------------------------------------
// SESSION TYPES — admin-managed priceable SKUs
// -----------------------------------------------------------

/** A bookable SKU. The booking modal's dropdown shows only types
 *  whose `discipline` matches the current member's flavor. */
export interface SessionType {
  id: string;
  slug: string;
  name: LocalisedString;
  description: LocalisedString;
  priceHuf: number;
  discipline: Discipline;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

// -----------------------------------------------------------
// BOOKINGS
// -----------------------------------------------------------

export type BookingStatus = 'pending' | 'approved' | 'declined' | 'cancelled';

export interface BookingOccurrence {
  id: string;
  bookingId: string;
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  decidedAt?: string | null;
  decidedBy?: string | null;
  decisionNote?: string;
}

export interface Booking {
  id: string;
  userId: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  eventId?: string | null;
  /** Optional FK to the session type used (NULL for legacy or
   *  free-form bookings). */
  sessionTypeId?: string | null;
  location: 'A22' | 'M15';
  startsAt: string;
  endsAt: string;
  status: BookingStatus;
  recurrenceCount: number;
  recurrenceUntil?: string | null;
  recurrenceWeekdays?: number[] | null;
  notes?: string;
  decidedAt?: string | null;
  decidedBy?: string | null;
  decisionNote?: string;
  createdAt?: string;
  occurrences?: BookingOccurrence[];
}

/**
 * A navbar item. Either references an existing page (`kind: 'page'`) or is a
 * free-form internal / external link (`kind: 'link'`). The public site
 * concatenates all `showInNav` items in `navOrder` order.
 */
export interface NavItem {
  id: string;
  kind: 'page' | 'link';
  /** For kind: 'page' — the id of the PageRecord this item mirrors. */
  pageId?: string;
  /** For kind: 'link' — display label, localised. */
  label: LocalisedString;
  /** For kind: 'link' — href. Internal if starts with /, external otherwise. */
  url: string;
  /** For kind: 'link' — open in a new tab. */
  openInNewTab?: boolean;
  /** Common. */
  showInNav: boolean;
  navOrder: number;
}

/** Full content tree returned by GET /api/content (or local mock). */
export interface ContentTree {
  pages: PageRecord[];
  /** Flat list of navbar items. Built-in pages get an auto-generated item
   *  on first read if the tree predates this field. */
  navItems: NavItem[];
  /** @deprecated — kept for backward-compat reads only. */
  navOrder?: string[];
  seo: Record<string, SeoOverride>;
  agenda: AgendaEvent[];
  /** Admin-managed priceable SKUs. Optional on legacy trees — readers
   *  should fall back to `DEFAULT_SESSION_TYPES` when absent. */
  sessionTypes?: SessionType[];
  meta: {
    online: boolean;
    fetchedAt: string;
  };
}

// -----------------------------------------------------------
// Default pages — visible immediately on first load, before
// the admin has saved anything.
// -----------------------------------------------------------

/** Sentinel id for the editable Main (home) page. The Main page has slug '' and
 *  is special-cased by the renderer; its navLabel doubles as the page <h1>. */
export const MAIN_PAGE_ID = 'main';

/** Sentinel id for the editable Yoga home page. Same routing as `MAIN_PAGE_ID`
 *  (both render at `/`); the renderer picks the right body based on the
 *  current mode. Kept as a separate record so the admin can author
 *  distinct content per mode. */
export const MAIN_YOGA_PAGE_ID = 'main-yoga';

/** Page ids that share the home route (`/`). The renderer picks the one
 *  matching the current mode. */
export const HOME_PAGE_IDS: ReadonlySet<string> = new Set([
  MAIN_PAGE_ID,
  MAIN_YOGA_PAGE_ID,
]);

export const DEFAULT_PAGES: PageRecord[] = [
  {
    id: MAIN_PAGE_ID,
    slug: '',
    navLabel: { hu: '333 Studio Budapest', en: '333 Studio Budapest' },
    showInNav: false,
    navOrder: 0,
    body: { hu: '', en: '' },
  },
  {
    id: MAIN_YOGA_PAGE_ID,
    // Slug is 'main-yoga' (not '') because `pages.slug` has a UNIQUE
    // constraint at the DB layer. The home renderer looks up by `id`
    // via `getHomePage()`, so this slug is never used for routing — it's
    // just a unique placeholder. The admin card suppresses the slug
    // display via the `--main-yoga` badge.
    slug: 'main-yoga',
    navLabel: { hu: '333 Yoga Budapest', en: '333 Yoga Budapest' },
    showInNav: false,
    navOrder: 0,
    body: { hu: '', en: '' },
  },
  {
    id: 'about',
    slug: 'about',
    navLabel: { hu: 'Rólunk', en: 'About' },
    showInNav: true,
    navOrder: 10,
    body: {
      hu: 'A 333 Studio nem egy átlagos terem. Három, egymásra épülő blokk — Strength, Conditioning és Recovery — egy rendszerben, két belvárosi helyszínen.',
      en: '333 Studio is not an average gym. Three interlocking blocks — Strength, Conditioning and Recovery — one system, two downtown locations.',
    },
  },
  {
    id: 'disciplines',
    slug: 'services',
    navLabel: { hu: 'Szolgáltatások', en: 'Services' },
    showInNav: true,
    navOrder: 20,
    body: {
      hu: 'Erő, állóképesség és regeneráció — egy rendszerben.',
      en: 'Strength, endurance, and recovery — one system.',
    },
  },
  {
    id: 'locations',
    slug: 'locations',
    navLabel: { hu: 'Helyszínek', en: 'Locations' },
    showInNav: true,
    navOrder: 30,
    body: {
      hu: 'Két belvárosi stúdió, egy edzésrendszer.',
      en: 'Two downtown studios, one training system.',
    },
  },
  {
    id: 'contact',
    slug: 'contact',
    navLabel: { hu: 'Kapcsolat', en: 'Contact' },
    showInNav: true,
    navOrder: 40,
    body: {
      hu: 'Írj nekünk — 24 órán belül válaszolunk.',
      en: 'Get in touch — we reply within 24 hours.',
    },
  },
];

// -----------------------------------------------------------
// Helpers
// -----------------------------------------------------------

export const getPageBySlug = (tree: ContentTree, slug: string): PageRecord | undefined =>
  tree.pages.find(p => p.slug === slug);

/** Default session types — mirrors the SQL seed in
 *  migrations/0003_session_types_and_member_discipline.sql so the
 *  admin UI has data to render before the API returns. */
export const DEFAULT_SESSION_TYPES: SessionType[] = [
  {
    id: 'st_str_pt',
    slug: 'strength-personal-training',
    name: { hu: 'Személyi edzés', en: 'Personal Training' },
    description: {
      hu: '1 órás egyéni edzés progresszív túlterheléssel.',
      en: '1-hour 1:1 session with progressive overload.',
    },
    priceHuf: 18000,
    discipline: 'strength',
    isActive: true,
    sortOrder: 10,
  },
  {
    id: 'st_str_cl',
    slug: 'strength-class',
    name: { hu: 'Csoportos edzés', en: 'Class' },
    description: {
      hu: 'Csoportos edzés max. 8 főnek.',
      en: 'Group class (max 8).',
    },
    priceHuf: 6000,
    discipline: 'strength',
    isActive: true,
    sortOrder: 20,
  },
  {
    id: 'st_str_mg',
    slug: 'strength-micro-group',
    name: { hu: 'Micro group (pár)', en: 'Micro Group (couples)' },
    description: {
      hu: '2 fős micro group — barátnak, párnak.',
      en: '2-person micro group for friends or partners.',
    },
    priceHuf: 12000,
    discipline: 'strength',
    isActive: true,
    sortOrder: 30,
  },
  {
    id: 'st_yog_cl',
    slug: 'yoga-class',
    name: { hu: 'Csoportos jóga', en: 'Yoga Class' },
    description: {
      hu: 'Csoportos jógaóra max. 12 főnek.',
      en: 'Group yoga class (max 12).',
    },
    priceHuf: 4500,
    discipline: 'yoga',
    isActive: true,
    sortOrder: 10,
  },
  {
    id: 'st_yog_pt',
    slug: 'yoga-personal',
    name: { hu: 'Személyi jóga', en: 'Private Yoga' },
    description: {
      hu: '1 órás egyéni jóga.',
      en: '1-hour 1:1 yoga.',
    },
    priceHuf: 14000,
    discipline: 'yoga',
    isActive: true,
    sortOrder: 20,
  },
  {
    id: 'st_yog_mg',
    slug: 'yoga-micro-group',
    name: { hu: 'Micro group (pár)', en: 'Micro Group (couples)' },
    description: {
      hu: '2 fős micro group — barátnak, párnak.',
      en: '2-person micro group for friends or partners.',
    },
    priceHuf: 9000,
    discipline: 'yoga',
    isActive: true,
    sortOrder: 30,
  },
];

export const visibleNavPages = (tree: ContentTree): PageRecord[] =>
  tree.pages
    .filter(p => p.showInNav)
    .sort((a, b) => a.navOrder - b.navOrder);

/**
 * Build the navbar items for an old-style tree that doesn't have `navItems`.
 * Used as a migration step on first read so legacy localStorage / D1 rows
 * still produce a navbar.
 */
export function ensureNavItems(tree: ContentTree): NavItem[] {
  if (Array.isArray(tree.navItems) && tree.navItems.length > 0) {
    // Backfill any default pages that lack an item.
    for (const p of tree.pages) {
      if (!tree.navItems.some(n => n.kind === 'page' && n.pageId === p.id)) {
        tree.navItems.push({
          id: crypto.randomUUID(),
          kind: 'page',
          pageId: p.id,
          label: { hu: '', en: '' },
          url: '',
          showInNav: p.showInNav,
          navOrder: p.navOrder,
        });
      }
    }
    return tree.navItems;
  }
  return tree.pages
    .slice()
    .sort((a, b) => a.navOrder - b.navOrder)
    .map<NavItem>(p => ({
      id: crypto.randomUUID(),
      kind: 'page',
      pageId: p.id,
      label: { hu: '', en: '' },
      url: '',
      showInNav: p.showInNav,
      navOrder: p.navOrder,
    }));
}

/** Items that should appear in the public navbar, in display order. */
export function visibleNavItems(tree: ContentTree): NavItem[] {
  const items = ensureNavItems(tree);
  return items.filter(i => i.showInNav).sort((a, b) => a.navOrder - b.navOrder);
}

export const newPage = (): PageRecord => ({
  id: crypto.randomUUID(),
  slug: '',
  navLabel: { hu: 'Új oldal', en: 'New page' },
  showInNav: true,
  navOrder: 50,
  body: { hu: '', en: '' },
});

export const newNavItem = (order = 100): NavItem => ({
  id: crypto.randomUUID(),
  kind: 'link',
  label: { hu: 'Új link', en: 'New link' },
  url: '/',
  openInNewTab: false,
  showInNav: true,
  navOrder: order,
});